"use client";

import { useEffect, useSyncExternalStore } from "react";
import { Logger } from "@cede/utils";

const WS_URL = "wss://api.hyperliquid.xyz/ws";
const HEARTBEAT_INTERVAL = 50_000; // 50 seconds (server timeout is 60s)
const MAX_RECONNECT_ATTEMPTS = 10;
const MAX_RECONNECT_DELAY = 30_000;
/** A handshake stuck in CONNECTING fires neither `open` nor `close` — bound it. */
const CONNECT_TIMEOUT_MS = 10_000;
/** A black-holed connection stays OPEN with no close event; HL pongs every ping,
 *  so two silent heartbeat windows mean the link is gone, not the market. */
const SILENCE_TIMEOUT_MS = HEARTBEAT_INTERVAL * 2;
/** Backoff resets only after a connection has carried data this long — wall-clock
 *  alone lets an accept-then-black-hole server reset the ladder every cycle. */
const STABLE_CONNECTION_MS = 30_000;
const WATCHDOG_INTERVAL = 5_000;

// --- Module-level singleton managing a single shared WebSocket connection ---

type SetupFn = (ws: WebSocket) => (() => void) | void;
type MessageListener = (data: unknown) => void;
type ErrorListener = (error: string) => void;
type StatusListener = () => void;

/**
 * A Hyperliquid WS subscription descriptor.
 * Examples:
 *   { type: "allMids" }
 *   { type: "l2Book", coin: "BTC" }
 *   { type: "l2Book", coin: "BTC", fast: true }
 *   { type: "candle", coin: "BTC", interval: "1m" }
 */
export type WSSubscription = Record<string, string | boolean>;

interface SubscriberEntry {
  setup?: SetupFn;
  onMessage?: MessageListener;
  onError?: ErrorListener;
  cleanup: (() => void) | null;
  /** Number of mounted hook instances sharing this subscriber */
  refCount: number;
  /** Deferred removal timeout — cancelled if a new instance re-mounts before it fires */
  pendingRemove?: NodeJS.Timeout;
}

interface SubscriptionEntry {
  subscription: WSSubscription;
  /** Number of active subscribers for this subscription */
  refCount: number;
  /** Deferred unsubscribe timeout — cancelled if a new subscriber re-adds before it fires */
  pendingUnsubscribe?: NodeJS.Timeout;
}

/**
 * Lifecycle of the shared socket, for UI that must distinguish "quiet market"
 * from "we lost the feed". `unavailable` is terminal until a manual retry, a tab
 * focus, or the browser coming back online.
 */
export type HyperliquidWsPhase = "idle" | "connecting" | "live" | "reconnecting" | "unavailable";

let ws: WebSocket | null = null;
let isConnecting = false;
let isConnected = false;
let phase: HyperliquidWsPhase = "idle";
let connectionError: string | null = null;
let reconnectAttempts = 0;
let reconnectTimeout: NodeJS.Timeout | undefined;
let heartbeatInterval: NodeJS.Timeout | undefined;
let disconnectTimeout: NodeJS.Timeout | undefined;
let connectTimeout: NodeJS.Timeout | undefined;
let watchdogInterval: NodeJS.Timeout | undefined;
/** Stamped on every inbound frame, pongs included — the watchdog's liveness signal. */
let lastMessageAt = 0;
let connectedSince = 0;

function setPhase(next: HyperliquidWsPhase) {
  if (phase === next) return;
  phase = next;
  notifyStatusListeners();
}

/**
 * Subscriber registry keyed by caller-provided `id`.
 * Multiple hook instances with the same `id` share a single subscriber entry
 * (callbacks registered once, ref-counted for lifecycle).
 */
const subscribers = new Map<string, SubscriberEntry>();

/**
 * Reference-counted subscription registry.
 * Key = stable JSON string of the subscription descriptor.
 * Subscribe WS message is sent only when refcount goes 0 → 1.
 * Unsubscribe WS message is deferred and sent only when refcount
 * stays at 0 after a microtask (survives React Strict Mode re-runs).
 */
const subscriptions = new Map<string, SubscriptionEntry>();
const statusListeners = new Set<StatusListener>();

function subKey(sub: WSSubscription): string {
  return JSON.stringify(sub, Object.keys(sub).sort());
}

function notifyStatusListeners() {
  statusListeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      Logger.error("[hyperliquidWS] Status listener threw:", err);
    }
  });
}

function sendWsSubscribe(sub: WSSubscription) {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ method: "subscribe", subscription: sub }));
  }
}

function sendWsUnsubscribe(sub: WSSubscription) {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ method: "unsubscribe", subscription: sub }));
  }
}

function runSetup(entry: SubscriberEntry) {
  if (!ws || ws.readyState !== WebSocket.OPEN || !entry.setup) return;
  entry.cleanup?.();
  const cleanup = entry.setup(ws);
  entry.cleanup = cleanup ?? null;
}

function runAllSetups() {
  subscribers.forEach((entry) => runSetup(entry));
}

function cleanupAllSubscribers() {
  subscribers.forEach((entry) => {
    entry.cleanup?.();
    entry.cleanup = null;
  });
}

function stopHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = undefined;
  }
}

function startHeartbeat() {
  stopHeartbeat();
  heartbeatInterval = setInterval(() => {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ method: "ping" }));
    }
  }, HEARTBEAT_INTERVAL);
}

function stopWatchdog() {
  if (watchdogInterval) {
    clearInterval(watchdogInterval);
    watchdogInterval = undefined;
  }
}

/** Covers what `onclose` cannot: a silent half-open socket, and a backoff ladder
 *  that never resets because the connection keeps flapping. */
function startWatchdog() {
  stopWatchdog();
  watchdogInterval = setInterval(() => {
    if (!ws || phase !== "live") return;
    const now = Date.now();

    // `lastMessageAt > connectedSince` proves a frame arrived on THIS connection.
    if (now - connectedSince >= STABLE_CONNECTION_MS && lastMessageAt > connectedSince && reconnectAttempts !== 0) {
      reconnectAttempts = 0;
    }

    if (now - lastMessageAt > SILENCE_TIMEOUT_MS) {
      Logger.error(
        `[hyperliquidWS] No frames for ${Math.round((now - lastMessageAt) / 1000)}s — closing half-open socket`,
      );
      // close() only reaches CLOSING here; without the phase flip the watchdog
      // re-fires every tick until the browser's closing timeout expires.
      setPhase("reconnecting");
      // Funnel into the normal onclose → reconnect path rather than duplicating it.
      ws.close();
    }
  }, WATCHDOG_INTERVAL);
}

function disconnect() {
  cleanupAllSubscribers();

  if (disconnectTimeout) {
    clearTimeout(disconnectTimeout);
    disconnectTimeout = undefined;
  }

  // Clear all pending unsubscribe timeouts
  subscriptions.forEach((entry) => {
    if (entry.pendingUnsubscribe) {
      clearTimeout(entry.pendingUnsubscribe);
    }
  });

  stopHeartbeat();
  stopWatchdog();

  if (connectTimeout) {
    clearTimeout(connectTimeout);
    connectTimeout = undefined;
  }

  if (ws) {
    ws.close();
    ws = null;
  }

  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = undefined;
  }

  isConnecting = false;
  isConnected = false;
  reconnectAttempts = 0;
  setPhase("idle");
  notifyStatusListeners();
}

function connect() {
  if (isConnecting || ws || subscribers.size === 0) return;

  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = undefined;
  }

  isConnecting = true;
  connectionError = null;
  setPhase(reconnectAttempts > 0 ? "reconnecting" : "connecting");
  notifyStatusListeners();

  try {
    const socket = new WebSocket(WS_URL);

    connectTimeout = setTimeout(() => {
      if (socket !== ws || socket.readyState !== WebSocket.CONNECTING) return;
      Logger.error("[hyperliquidWS] Handshake timed out — aborting and retrying");
      socket.close();
    }, CONNECT_TIMEOUT_MS);

    socket.onopen = () => {
      // A socket we already abandoned (handshake timeout, give-up) must not be
      // able to report the connection live from under its replacement.
      if (socket !== ws) {
        socket.close();
        return;
      }

      isConnected = true;
      isConnecting = false;
      connectedSince = Date.now();
      lastMessageAt = Date.now();

      if (connectTimeout) {
        clearTimeout(connectTimeout);
        connectTimeout = undefined;
      }

      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      // Not reset here: the watchdog clears it once the connection has carried
      // data for STABLE_CONNECTION_MS.
      startHeartbeat();
      startWatchdog();
      setPhase("live");
      notifyStatusListeners();

      // Re-subscribe all active subscriptions
      subscriptions.forEach((entry) => {
        if (entry.refCount > 0) {
          sendWsSubscribe(entry.subscription);
        }
      });

      // Run all setup callbacks
      runAllSetups();
    };

    socket.onmessage = (event) => {
      // An abandoned socket must not stamp the replacement's liveness clock.
      if (socket !== ws) return;
      // Stamped before any filtering so pongs count as liveness.
      lastMessageAt = Date.now();

      let msg: { channel?: string };
      try {
        msg = JSON.parse(event.data);
      } catch {
        // Non-JSON frame — nothing to dispatch.
        return;
      }

      if (msg.channel === "subscriptionResponse" || msg.channel === "pong") return;

      // A rejected subscription arrives here and nowhere else: HL answers it with
      // an error frame, not a close, so without this the feed is simply silent.
      if (msg.channel === "error") {
        Logger.error("[hyperliquidWS] Server error frame:", event.data);
        return;
      }

      subscribers.forEach((entry) => {
        try {
          entry.onMessage?.(msg);
        } catch (err) {
          Logger.error("[hyperliquidWS] Subscriber onMessage threw:", err);
        }
      });
    };

    socket.onerror = () => {
      if (socket !== ws) return;

      isConnecting = false;
      isConnected = false;
      const errorMsg = "WebSocket connection error";
      connectionError = errorMsg;
      notifyStatusListeners();
      subscribers.forEach((entry) => entry.onError?.(errorMsg));
    };

    socket.onclose = () => {
      if (socket !== ws) return;

      isConnecting = false;
      isConnected = false;
      ws = null;
      stopHeartbeat();
      stopWatchdog();
      if (connectTimeout) {
        clearTimeout(connectTimeout);
        connectTimeout = undefined;
      }
      cleanupAllSubscribers();
      notifyStatusListeners();

      if (subscribers.size > 0 && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), MAX_RECONNECT_DELAY);
        reconnectAttempts++;
        setPhase("reconnecting");

        Logger.log(
          `[hyperliquidWS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
          Logger.types.INFO,
        );

        reconnectTimeout = setTimeout(() => {
          connect();
        }, delay);
      } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        const errorMsg = "Failed to reconnect after multiple attempts";
        setPhase("unavailable");
        Logger.error("[hyperliquidWS] Max reconnection attempts reached, giving up");
        connectionError = errorMsg;
        notifyStatusListeners();
        subscribers.forEach((entry) => entry.onError?.(errorMsg));
      } else {
        setPhase("idle");
      }
    };

    ws = socket;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : "Unknown error";
    Logger.error("[hyperliquidWS] Failed to create WebSocket connection:", errorMessage, err);
    isConnecting = false;
    const errorMsg = "Failed to create WebSocket connection";
    connectionError = errorMsg;
    setPhase("unavailable");
    notifyStatusListeners();
    subscribers.forEach((entry) => entry.onError?.(errorMsg));
  }
}

/**
 * Increment the refcount for a subscription.
 * Sends a WS subscribe message only on the 0 → 1 transition.
 */
function refSubscription(sub: WSSubscription) {
  const key = subKey(sub);
  const existing = subscriptions.get(key);

  if (existing) {
    // Cancel any pending deferred unsubscribe (React Strict Mode re-mount)
    if (existing.pendingUnsubscribe) {
      clearTimeout(existing.pendingUnsubscribe);
      existing.pendingUnsubscribe = undefined;
    }
    existing.refCount++;
  } else {
    // First subscriber for this key — send subscribe
    subscriptions.set(key, { subscription: sub, refCount: 1 });
    sendWsSubscribe(sub);
  }
}

/**
 * Decrement the refcount for a subscription.
 * When it reaches 0, defers the WS unsubscribe message so that
 * React Strict Mode cleanup → re-mount cycles don't cause unnecessary
 * unsubscribe/subscribe churn.
 */
function unrefSubscription(sub: WSSubscription) {
  const key = subKey(sub);
  const entry = subscriptions.get(key);
  if (!entry) return;

  entry.refCount--;

  if (entry.refCount <= 0) {
    // Defer unsubscribe so React effect re-subscriptions can cancel it
    entry.pendingUnsubscribe = setTimeout(() => {
      entry.pendingUnsubscribe = undefined;
      // Double-check nobody re-subscribed during the deferral
      if (entry.refCount <= 0) {
        sendWsUnsubscribe(entry.subscription);
        subscriptions.delete(key);
      }
    }, 0);
  }
}

type SubscriberHandlers = Pick<SubscriberEntry, "setup" | "onMessage" | "onError">;

/**
 * Re-point an already-registered subscriber's handlers at the CURRENT callbacks.
 *
 * Consumers register with a STATIC `id` (e.g. "clearinghouseState") while their
 * `onMessage`/`onError` close over `userAddress`-specific React Query keys. On a
 * wallet/account switch the hook re-renders with new callbacks but the same `id`;
 * without this refresh the dispatch loop would keep invoking the PREVIOUS user's
 * closure and write live WS events into the wrong user's cache.
 *
 * Mutates the existing entry in place so the socket lifecycle (subKey-keyed
 * subscribe/unsubscribe, refcount, deferred removal) is untouched — only the handler
 * identities change. `setup` is refreshed for any future re-run (reconnect/resubscribe)
 * but intentionally NOT re-invoked here, to avoid tearing down/recreating the imperative
 * subscription on every render. No-op if the id isn't registered yet.
 *
 * @internal exported for unit testing the latest-ref behaviour.
 */
export function refreshSubscriberHandlers(id: string, handlers: SubscriberHandlers) {
  const entry = subscribers.get(id);
  if (!entry) return;
  entry.setup = handlers.setup;
  entry.onMessage = handlers.onMessage;
  entry.onError = handlers.onError;
}

/**
 * Increment the refcount for a subscriber by `id`.
 * On the first mount (0 → 1), registers the subscriber with its callbacks
 * and opens the WS connection if needed.
 */
function refSubscriber(id: string, subscriber: Omit<SubscriberEntry, "refCount" | "cleanup">) {
  // Cancel any pending deferred disconnect
  if (disconnectTimeout) {
    clearTimeout(disconnectTimeout);
    disconnectTimeout = undefined;
  }

  const existing = subscribers.get(id);

  if (existing) {
    // Cancel any pending deferred removal
    if (existing.pendingRemove) {
      clearTimeout(existing.pendingRemove);
      existing.pendingRemove = undefined;
    }
    existing.refCount++;
    // Latest-ref: keep this id pointed at the current callbacks (account switch).
    refreshSubscriberHandlers(id, subscriber);
    // Gated on the terminal state so a mount can't short-circuit a live backoff.
    if (phase === "unavailable") {
      reconnectAttempts = 0;
      connect();
    }
    return;
  }

  // First mount for this id — register subscriber
  const entry: SubscriberEntry = {
    setup: subscriber.setup,
    onMessage: subscriber.onMessage,
    onError: subscriber.onError,
    cleanup: null,
    refCount: 1,
  };
  subscribers.set(id, entry);

  if (isConnected && ws) {
    runSetup(entry);
  } else if (subscribers.size === 1) {
    connect();
  }
}

/**
 * Decrement the refcount for a subscriber by `id`.
 * When it reaches 0, defers removal so React Strict Mode
 * cleanup → re-mount cycles don't cause unnecessary churn.
 */
function unrefSubscriber(id: string) {
  const entry = subscribers.get(id);
  if (!entry) return;

  entry.refCount--;

  if (entry.refCount <= 0) {
    entry.pendingRemove = setTimeout(() => {
      entry.pendingRemove = undefined;
      if (entry.refCount <= 0) {
        // Actually remove
        entry.cleanup?.();
        entry.cleanup = null;
        subscribers.delete(id);

        // Defer disconnect if no subscribers remain
        if (subscribers.size === 0) {
          if (disconnectTimeout) clearTimeout(disconnectTimeout);
          disconnectTimeout = setTimeout(() => {
            disconnectTimeout = undefined;
            if (subscribers.size === 0) {
              disconnect();
            }
          }, 0);
        }
      }
    }, 0);
  }
}

function getSnapshot() {
  return { isConnected, error: connectionError, phase };
}

// Stable reference that only changes when the values change
let cachedSnapshot = getSnapshot();

function getSnapshotStable() {
  const next = getSnapshot();
  if (
    next.isConnected !== cachedSnapshot.isConnected ||
    next.error !== cachedSnapshot.error ||
    next.phase !== cachedSnapshot.phase
  ) {
    cachedSnapshot = next;
  }
  return cachedSnapshot;
}

function subscribeToStatus(listener: StatusListener) {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

// Visibility handling (module-level, registered once)
let visibilityHandlerRegistered = false;

function registerVisibilityHandler() {
  if (visibilityHandlerRegistered || typeof window === "undefined") return;
  visibilityHandlerRegistered = true;

  window.addEventListener("visibilitychange", () => {
    if (subscribers.size === 0) return;

    if (document.visibilityState === "hidden") {
      disconnect();
    } else {
      // A tab returning to the foreground is a fresh chance, including after we
      // gave up — otherwise `unavailable` would outlive the outage that caused it.
      reconnectAttempts = 0;
      connect();
    }
  });

  window.addEventListener("online", () => {
    if (subscribers.size === 0 || document.visibilityState === "hidden") return;
    reconnectAttempts = 0;
    connect();
  });
}

// --- Imperative API (non-React consumers, e.g. the TradingView datafeeds) ---

export interface HyperliquidWsStatus {
  phase: HyperliquidWsPhase;
  error: string | null;
  /** Epoch ms of the last inbound frame (pongs included), 0 before the first. */
  lastMessageAt: number;
}

/**
 * Point-in-time status. `lastMessageAt` is deliberately absent from the reactive
 * snapshot — it changes on every frame, so a subscriber would re-render per tick;
 * poll this instead on whatever cadence the display needs.
 */
export function getHyperliquidWsStatus(): HyperliquidWsStatus {
  return { phase, error: connectionError, lastMessageAt };
}

/** Subscribe to phase/error transitions. Returns an unsubscribe function. */
export function subscribeToHyperliquidWsStatus(listener: () => void): () => void {
  return subscribeToStatus(listener);
}

/** Manual retry, for a "Reconnect" affordance once the phase is `unavailable`. */
export function reconnectHyperliquidWs() {
  reconnectAttempts = 0;
  if (ws) {
    const abandoned = ws;
    ws = null;
    abandoned.onclose = null;
    abandoned.close();
    stopHeartbeat();
    stopWatchdog();
    isConnected = false;
    setPhase("reconnecting");
  }
  // A retry pressed mid-handshake would otherwise be swallowed by connect()'s
  // own `isConnecting` guard, with the socket already abandoned and no timer left.
  if (connectTimeout) {
    clearTimeout(connectTimeout);
    connectTimeout = undefined;
  }
  isConnecting = false;
  connect();
}

export interface HyperliquidWsSubscriberOptions {
  /** Subscriber identity. Ref-counted: same id twice registers the callbacks once. */
  id: string;
  subscription?: WSSubscription;
  setup?: SetupFn;
  onMessage?: MessageListener;
  onError?: ErrorListener;
}

/**
 * Register a subscriber outside React and return its release function, for
 * consumers with no component to hang an effect on (the chart datafeeds are
 * plain factories). Callers must release: reconnect-forever turns a leaked
 * subscription into a permanent one.
 */
export function subscribeHyperliquidWs({
  id,
  subscription,
  setup,
  onMessage,
  onError,
}: HyperliquidWsSubscriberOptions): () => void {
  registerVisibilityHandler();
  if (subscription) refSubscription(subscription);
  refSubscriber(id, { setup, onMessage, onError });

  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (subscription) unrefSubscription(subscription);
    unrefSubscriber(id);
  };
}

// --- React hook wrapping the singleton ---

interface UseHyperliquidWsConnectionProps {
  /**
   * Unique identifier for this subscriber.
   * Multiple hook instances with the same `id` share a single subscriber
   * (callbacks registered once, ref-counted for lifecycle).
   */
  id: string;
  /**
   * Subscription descriptor sent to Hyperliquid WS.
   * Reference-counted: multiple hooks subscribing to the same descriptor
   * share a single WS subscription (only one subscribe message is sent,
   * unsubscribe only fires when the last hook unmounts).
   */
  subscription?: WSSubscription;
  /**
   * Called when the WS is open (or on reconnect).
   * Should return a cleanup function.
   * Use for custom imperative logic beyond declarative subscriptions.
   */
  setup?: SetupFn;
  /** Called for every incoming WS message (after pong/subscriptionResponse filtering). */
  onMessage?: MessageListener;
  enabled?: boolean;
  onError?: ErrorListener;
}

interface UseHyperliquidWsConnectionResult {
  isConnected: boolean;
  error: string | null;
  phase: HyperliquidWsPhase;
}

/**
 * Shared Hyperliquid WebSocket connection hook.
 *
 * All hook instances share a single module-level WebSocket connection.
 * The connection opens when the first consumer mounts and closes when the last unmounts.
 * Heartbeat, reconnection, and visibility handling are managed centrally.
 *
 * Subscribers are deduplicated by `id`: if multiple components call this hook
 * with the same `id`, the `setup`/`onMessage`/`onError` callbacks are registered
 * only once. The subscriber is removed only when all instances with that `id` unmount.
 *
 * Two subscription modes (can be combined):
 *
 * 1. **Declarative** (`subscription`): Deduplicated via reference counting.
 *    Multiple hooks subscribing to `{ type: "allMids" }` send only one
 *    subscribe message to the server.
 *
 * 2. **Imperative** (`setup`): Called when the WS is open.
 *    Should return a cleanup function.
 *
 * @example
 * ```ts
 * const { isConnected } = useHyperliquidWsConnection({
 *   id: "allMids",
 *   subscription: { type: "allMids" },
 *   onMessage: (msg) => { ... },
 * });
 * ```
 */
export const useHyperliquidWsConnection = ({
  id,
  subscription,
  setup,
  onMessage,
  enabled = true,
  onError,
}: UseHyperliquidWsConnectionProps): UseHyperliquidWsConnectionResult => {
  // The server snapshot is the same one: no effect runs there, so the socket is
  // always closed and the client's first render agrees. Omitting it made React
  // throw during SSR of every page that mounts this hook.
  const {
    isConnected: connected,
    error,
    phase: currentPhase,
  } = useSyncExternalStore(subscribeToStatus, getSnapshotStable, getSnapshotStable);

  // Declarative subscription — reference-counted, deduplicated
  useEffect(() => {
    if (!enabled || !subscription) return;

    registerVisibilityHandler();
    refSubscription(subscription);

    return () => {
      unrefSubscription(subscription);
    };
    // subscription is an object literal — stringify for stable deps
  }, [enabled, subKey(subscription ?? {})]);

  // Subscriber registration — ref-counted by id.
  useEffect(() => {
    if (!enabled) return;

    registerVisibilityHandler();
    refSubscriber(id, { setup, onMessage, onError });

    return () => {
      unrefSubscriber(id);
    };
    // Ref-count lifecycle is keyed by `id`/`enabled` only — the socket subscription
    // must not churn when callbacks change identity.
  }, [enabled, id]);

  // Latest-ref handler refresh on every render.
  //
  // Consumers keep a STATIC `id` across account switches while their callbacks close
  // over `userAddress`-specific data. The ref-count effect above (deps `[enabled, id]`)
  // does NOT re-run on a callback change, so on its own it would leave the stale
  // closure registered and route live WS events into the previous user's cache.
  // Re-pointing the registered entry at the current handlers each render fixes this
  // without re-subscribing the socket.
  useEffect(() => {
    if (!enabled) return;
    refreshSubscriberHandlers(id, { setup, onMessage, onError });
  });

  return {
    isConnected: enabled && connected,
    error: enabled ? error : null,
    phase: enabled ? currentPhase : "idle",
  };
};
